import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, RefreshCw, HardDrive, Play, Mail, CheckCircle2, XCircle, AlertTriangle, Database } from "lucide-react";

/**
 * AdminBackups — standalone page that mirrors the Backups sub-tab of the
 * Security Center. Exposed at /admin/backups so admins on both Core and Pro
 * can run / download backups.
 *
 * The /api/backup/* endpoints accept either an admin or a vendor session
 * (require_admin_or_master).
 */
const AdminBackups = () => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "—");

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/backup/history?limit=20`);
      setLogs(r.data.logs || []);
      setFiles(r.data.local_files || []);
    } catch (e) {
      // 401/403 — surface a friendly message.
      if (e.response?.status === 403 || e.response?.status === 401) {
        toast.error("You need admin or vendor access to view backups.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBackups(); }, []); // eslint-disable-line

  const runBackupNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/backup/run-now`);
      toast.success("Backup created successfully");
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup failed");
    } finally {
      setRunning(false);
    }
  };

  const downloadLatest = async () => {
    try {
      const response = await axios.post(`${API}/backup/download-latest`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "").substring(0, 15);
      a.href = url;
      a.download = `cpi_backup_${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded. Save it somewhere safe!");
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await axios.get(`${API}/backup/download/${filename}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const totalKB = files.reduce((s, f) => s + (f.size_kb || 0), 0).toFixed(0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Database className="h-6 w-6 text-amber-400" /> Database Backups
          </h1>
          <p className="text-sm text-gray-400">
            Daily automatic backup runs at <b>2:00 AM IST</b>. You can also create and download a fresh backup at any time.
          </p>
        </div>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-6 space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-2">
              <Button onClick={fetchBackups} disabled={loading} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="backups-refresh-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button onClick={runBackupNow} disabled={running} variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" data-testid="backups-run-now-btn">
                <Play className={`h-4 w-4 mr-1 ${running ? "animate-pulse" : ""}`} /> {running ? "Running..." : "Run Backup Now"}
              </Button>
              <Button onClick={downloadLatest} className="bg-amber-600 hover:bg-amber-700" data-testid="backups-download-fresh-btn">
                <Download className="h-4 w-4 mr-1" /> Download Fresh Backup
              </Button>
            </div>

            <div className="bg-emerald-500/10 border-l-4 border-emerald-500 p-3 rounded text-xs text-emerald-200">
              <CheckCircle2 className="h-4 w-4 inline mr-1" />
              <b>Protect your data:</b> Click <b>Download Fresh Backup</b> at least once a week. Save the JSON file to your email drafts, Dropbox, or Google Drive. If anything ever happens, the entire database can be restored from one of these files in minutes.
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <HardDrive className="h-4 w-4" /> Available Downloads
                <span className="text-xs font-normal text-gray-500">({files.length} files, {totalKB} KB total)</span>
              </h4>
              <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-900">
                    <TableRow className="border-gray-800 hover:bg-transparent">
                      <TableHead className="text-gray-400 text-xs">File</TableHead>
                      <TableHead className="text-gray-400 text-xs">Size</TableHead>
                      <TableHead className="text-gray-400 text-xs">Created</TableHead>
                      <TableHead className="text-gray-400 text-xs text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {files.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-6">No local backups yet. Click "Run Backup Now" to create one.</TableCell></TableRow>
                    ) : files.map((f, i) => (
                      <TableRow key={i} className="border-gray-800 hover:bg-gray-800/30" data-testid={`backups-row-${i}`}>
                        <TableCell className="text-xs text-cyan-300 font-mono">{f.filename}</TableCell>
                        <TableCell className="text-xs text-gray-300 font-mono">{f.size_kb} KB</TableCell>
                        <TableCell className="text-xs text-gray-400 font-mono">{fmtDate(f.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button onClick={() => downloadFile(f.filename)} size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10 h-7" data-testid={`backups-download-${i}`}>
                            <Download className="h-3 w-3 mr-1" /> Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email Delivery History
              </h4>
              <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-900">
                    <TableRow className="border-gray-800 hover:bg-transparent">
                      <TableHead className="text-gray-400 text-xs">Time</TableHead>
                      <TableHead className="text-gray-400 text-xs">File</TableHead>
                      <TableHead className="text-gray-400 text-xs">Size</TableHead>
                      <TableHead className="text-gray-400 text-xs">Emailed?</TableHead>
                      <TableHead className="text-gray-400 text-xs">Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No backup history yet.</TableCell></TableRow>
                    ) : logs.map((l, i) => (
                      <TableRow key={i} className="border-gray-800 hover:bg-gray-800/30">
                        <TableCell className="text-xs text-gray-300 font-mono">{fmtDate(l.timestamp)}</TableCell>
                        <TableCell className="text-xs text-cyan-300 font-mono truncate max-w-[200px]">{l.file}</TableCell>
                        <TableCell className="text-xs text-gray-300 font-mono">{l.size_kb} KB</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${l.emailed ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-gray-500/20 text-gray-300 border-gray-500/40"}`}>
                            {l.emailed ? (<><CheckCircle2 className="h-3 w-3 mr-1" /> Sent</>) : (<><XCircle className="h-3 w-3 mr-1" /> Local only</>)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-400 max-w-[250px] truncate" title={l.error || l.emailed_to}>{l.emailed_to || l.error || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {logs.some(l => l.emailed) ? (
              <div className="bg-green-500/10 border-l-4 border-green-500 p-3 rounded text-xs text-green-200" data-testid="backups-email-active-banner">
                <CheckCircle2 className="h-4 w-4 inline mr-1" /> <b>Email backups are active.</b> Each nightly backup is also emailed as an attachment to your configured recipient. Keep email as a second off-site copy.
              </div>
            ) : (
              <div className="bg-blue-500/10 border-l-4 border-blue-500 p-3 rounded text-xs text-blue-200" data-testid="backups-email-setup-banner">
                <AlertTriangle className="h-4 w-4 inline mr-1" /> <b>To enable automatic email backups</b>, ask your vendor to set up SMTP credentials.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBackups;
